import React, { useState, useEffect, useMemo } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  Typography,
  Stack,
  Box,
  InputAdornment,
  Button,
  Autocomplete,
  CircularProgress,
  Backdrop,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import api from "api";
import CodeListSection from "components/shared/CodeListSection";
import { debounce } from "lodash";
import { formErrorStrfromObj } from "utils/shared";

const DiagnosisCodeDialog = ({
  open,
  dxCodes,
  onClose,
  onSave,
  readOnly = false,
}) => {
  const [initialLoading, setInitialLoading] = useState(true);
  const [codeRangeStart, setCodeRangeStart] = useState("");
  const [codeRangeEnd, setCodeRangeEnd] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [selectedCodes, setSelectedCodes] = useState([]);
  const [errorMessage, setErrorMessage] = useState("");

  useEffect(() => {
    const processDxCodes = debounce(async (dxCodesExpanded) => {
      setInitialLoading(true);
      let processedDx;
      if (typeof dxCodesExpanded === "string") {
        processedDx = dxCodesExpanded.split(",").map((code) => code.trim());
      } else if (Array.isArray(dxCodesExpanded)) {
        processedDx = dxCodesExpanded;
      } else {
        processedDx = [];
      }

      processedDx = [...new Set(processedDx)];
      try {
        const descriptions =
          await api.diagnosisCode.getDiagnosisCodeDescriptions(processedDx);

        const codesWithDescriptions = processedDx.map((code, index) => ({
          icdDiagnosisKey: descriptions.data[index]?.icdDiagnosisKey || null,
          code,
          description: descriptions.data[index]?.icdShortDescription || "",
        }));
        setSelectedCodes(codesWithDescriptions);
      } catch (error) {
        console.error("Error fetching diagnosis descriptions:", error);
      } finally {
        setInitialLoading(false); // End initial loading indicator
      }
    }, 300);
    if (open && dxCodes?.dxCodesExpanded) {
      processDxCodes(dxCodes.dxCodesExpanded);
    } else {
      setInitialLoading(false); // End initial loading indicator
    }
  }, [open, dxCodes]);

  const handleSearchChange = (event, value, reason) => {
    if (event) {
      if (reason === "input" || reason === "clear") {
        setSearchTerm(value);
      }
    }
    if (!value) {
      setErrorMessage("");
    }
  };

  const handleKeyDown = (event) => {
    if (event && event.key === "Enter") {
      event.preventDefault(); // Prevent default Enter key behavior
      if (searchTerm.length >= 2) {
        performSearch(false); // Trigger the search
      } else {
        setDropdownOpen(false);
      }
    }
  };

  const fetchExpangedRange = async (start, end) => {
    if (!start.length || !end.length) return;
    setLoading(true);
    setErrorMessage("");
    try {
      const response = await api.diagnosisCode.getDiagnosisCodeRange(
        start,
        end
      );
      const list = response.data;
      if (list.length > 0) {
        const mappedResults = list.map((item) => ({
          icdDiagnosisKey: item.icdDiagnosisKey,
          code: item.diagnosisCode,
          description: item.icdShortDescription,
        }));
        return mappedResults;
      } else {
        setErrorMessage("Invalid code range.");
      }
      return [];
    } catch (error) {
      const message =
        formErrorStrfromObj(error) || "An error occurred during the search.";
      setErrorMessage(message);
      return [];
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    if (!searchTerm.length) setSearchResults([]);
  }, [searchTerm.length]);

  const performSearch = useMemo(
    () =>
      debounce(async (rangeSearch) => {
        if (searchTerm.length < 2) return;
        setLoading(true);
        setErrorMessage("");
        try {
          let response;
          if (rangeSearch) {
            response = await api.diagnosisCode.getDiagnosisCodeRangeList(
              codeRangeStart,
              codeRangeEnd
            );
          } else {
            response = await api.diagnosisCode.getDiagnosisCodeList(
              searchTerm,
              1,
              7
            );
          }

          const results = response.data.listDto;
          if (results.length > 0) {
            const mappedResults = results.map((item) => ({
              icdDiagnosisKey: item.icdDiagnosisKey,
              code: item.diagnosisCode,
              description: item.icdShortDescription,
            }));
            setSearchResults(mappedResults);
            setDropdownOpen(true);
          } else {
            setDropdownOpen(false);
            setErrorMessage("No matching Dx code found.");
          }
        } catch (error) {
          console.error("Error fetching procedure codes:", error);
          const message =
            error.response?.data?.errors[0] ||
            "An error occurred during the search.";
          setErrorMessage(message);
        } finally {
          setLoading(false);
        }
      }, 1000),
    [searchTerm, codeRangeStart, codeRangeEnd]
  );

  useEffect(() => {
    performSearch(false);
  }, [performSearch]);

  useEffect(() => {
    return () => {
      performSearch.cancel();
    };
  }, [performSearch]);

  const handleChange = (_, value, reason) => {
    if (value && reason === "selectOption") {
      setSelectedCodes((prev) => [...prev, value]);
      setDropdownOpen(false);
    }
  };

  const handleDelete = (index) => {
    setSelectedCodes((currentList) =>
      currentList.filter((_, i) => i !== index)
    );
  };

  const handleAddCodeRange = async () => {
    if (codeRangeStart.length > 0 && codeRangeEnd.length > 0) {
      const listRange = await fetchExpangedRange(codeRangeStart, codeRangeEnd);
      setSelectedCodes((currentList) => [...currentList, ...listRange]);
      setCodeRangeStart("");
      setCodeRangeEnd("");
    }
  };

  const handleSave = () => {
    const codesToSave = selectedCodes.map((item) => ({
      icdDiagnosisKey: item.icdDiagnosisKey,
      code: item.code,
      description: item.description,
    }));
    onSave(codesToSave);
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h4" sx={{ fontSize: "1.5rem" }}>
            Diagnosis Codes
          </Typography>
          <IconButton onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent>
        <Backdrop open={initialLoading} style={{ zIndex: 1000 }}>
          <CircularProgress color="inherit" />
        </Backdrop>
        {!initialLoading && (
          <>
            {!readOnly && (
              <>
                <Typography variant="body1" gutterBottom>
                  Type two or more characters to search for and add diagnosis
                  code(s) to policy.
                </Typography>
                <Typography sx={{ fontWeight: "bold" }} gutterBottom>
                  Add Single Code:
                </Typography>
                <Autocomplete
                  sx={{
                    "& .MuiTextField-root": {
                      mt: "0px",
                      mb: "16px",
                    },
                  }}
                  filterOptions={(x) => x}
                  freeSolo
                  options={searchResults}
                  getOptionLabel={(option) =>
                    option ? `${option.code} - ${option.description}` : ""
                  }
                  inputValue={searchTerm}
                  onInputChange={handleSearchChange}
                  onChange={handleChange}
                  open={dropdownOpen}
                  disableCloseOnSelect
                  onOpen={() => setDropdownOpen(true)}
                  onClose={() => setDropdownOpen(false)}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      placeholder="Diagnosis Code or Description Search"
                      label="Diagnosis Code or Description Search"
                      margin="normal"
                      variant="outlined"
                      onChange={handleSearchChange}
                      onKeyDown={handleKeyDown}
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {loading ? (
                              <CircularProgress color="inherit" size={20} />
                            ) : null}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                />
                {errorMessage && (
                  <Typography color="error" align="center">
                    {errorMessage}
                  </Typography>
                )}
                <Typography sx={{ fontWeight: "bold" }} gutterBottom>
                  Add Code Range:
                </Typography>

                <Stack>
                  <Stack
                    sx={{ marginBottom: "-7px" }}
                    direction={"row"}
                    justifyContent={"space-between"}
                  >
                    <Typography
                      sx={{ fontWeight: "bold", mr: "auto" }}
                      gutterBottom
                    >
                      Start Range:
                    </Typography>
                    <Typography
                      sx={{
                        fontWeight: "bold",
                        mr: "auto",
                        position: "relative",
                        left: "-15px",
                      }}
                      gutterBottom
                    >
                      End Range:
                    </Typography>
                  </Stack>
                  <Stack direction={"row"} spacing={2} alignItems={"center"}>
                    <TextField
                      variant="outlined"
                      placeholder="Start with"
                      label="Start with"
                      value={codeRangeStart}
                      onChange={(e) => setCodeRangeStart(e.target.value)}
                      InputProps={{
                        endAdornment: codeRangeStart && (
                          <InputAdornment position="end">
                            <IconButton
                              onClick={() => setCodeRangeStart("")}
                              edge="end"
                            >
                              <CloseIcon />
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                    />
                    <span>to</span>
                    <TextField
                      variant="outlined"
                      placeholder="End of Range"
                      label="End of Range"
                      value={codeRangeEnd}
                      onChange={(e) => setCodeRangeEnd(e.target.value)}
                      InputProps={{
                        endAdornment: codeRangeEnd && (
                          <InputAdornment position="end">
                            <IconButton
                              onClick={() => setCodeRangeEnd("")}
                              edge="end"
                            >
                              <CloseIcon />
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                    />
                    <IconButton onClick={handleAddCodeRange}>
                      <AddCircleOutlineIcon fontSize="large" color="grey" />
                    </IconButton>
                  </Stack>
                </Stack>
              </>
            )}

            <CodeListSection
              selectedCodes={selectedCodes}
              dxCodes={dxCodes}
              handleDelete={handleDelete}
              readOnly={readOnly}
            />
            {!readOnly && (
              <Typography>Total count: {selectedCodes.length}</Typography>
            )}
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        {!readOnly && (
          <Button color="primary" onClick={handleSave}>
            Add Code List
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default DiagnosisCodeDialog;
